import React from 'react';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { describeJoiningToday, formatOrdinal } from '../utils/billingDates';
import type { FrequencyInterval } from '../utils/billingDates';
import type { BillingMode, ReleaseDate } from './PackSizesPanel';
interface FrequencyBillingDateSectionProps {
  idPrefix: string;
  billingMode: BillingMode;
  fixedDay: number;
  // Dates of the pack's releases, used for the joining-today preview
  releaseDates: ReleaseDate[];
  cutoffDays: number;
  // The pack's frequencies, used to preview the next charge on Rolling, fixed day
  frequencies: FrequencyInterval[];
  onChange: (updates: {
    billingMode?: BillingMode;
    fixedDay?: number;
    cutoffDays?: number;
  }) => void;
}
const BILLING_MODE_OPTIONS: {
  id: BillingMode;
  title: string;
  description: string;
}[] = [{
  id: 'rolling',
  title: 'Rolling',
  description: "Bills on the member's sign-up date, then every interval after."
}, {
  id: 'fixedDay',
  title: 'Rolling, fixed day',
  description: "Keeps each member's own cycle, but every charge lands on the same day of the month."
}, {
  id: 'releaseDates',
  title: 'Fixed release dates',
  description: 'Every member bills on the same dates, no matter when they joined.'
}];
const DAYS = Array.from({
  length: 28
}, (_, index) => index + 1);
const selectClass = 'w-full h-[36px] pl-3 pr-8 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer';
export function FrequencyBillingDateSection({
  idPrefix,
  billingMode,
  fixedDay,
  releaseDates,
  cutoffDays,
  frequencies,
  onChange
}: FrequencyBillingDateSectionProps) {
  const isReleaseMode = billingMode === 'releaseDates';
  const isAligned = billingMode !== 'rolling';
  return <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-id="element-3281" data-mp="1kn3g58xdhlbh">
      <fieldset data-id="element-3282" data-mp="1pkv4jsyt6pax">
        <legend className="sr-only" data-id="element-3283" data-mp="1pleotgbxlps4">Billing date</legend>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" role="radiogroup" data-id="element-3284" data-mp="3r9iwv402ieua">
          {BILLING_MODE_OPTIONS.map(option => {
          const isSelected = billingMode === option.id;
          return <button key={option.id} type="button" role="radio" aria-checked={isSelected} onClick={() => onChange({
            billingMode: option.id
          })} className={`text-left p-3 rounded-lg border transition-colors ${isSelected ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1] hover:bg-[#F8FAFC]'}`} data-id="element-3285" data-mp="3hiy2jsrffwh6">
                <div className="flex items-center gap-2" data-id="element-3286" data-mp="2yklr20d1maif">
                  <span className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-3287" data-mp="2yl3t5dmdziy9">
                    {isSelected && <span className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-3288" data-mp="wrxj5dpgeywb" />}
                  </span>
                  <span className="text-[13px] font-semibold text-[#0F172A]" data-id="element-3289" data-mp="ssygy0v3xg8k">
                    {option.title}
                  </span>
                </div>
                <p className="text-[12px] text-[#64748B] mt-1 pl-6" data-id="element-3290" data-mp="2fo3dhggvpr81">
                  {option.description}
                </p>
              </button>;
        })}
        </div>
      </fieldset>

      {billingMode === 'fixedDay' && <div className="mt-4 max-w-[200px]" data-id="element-3291" data-mp="2ogwubibz4yo0">
          <label htmlFor={`${idPrefix}-fixed-day`} className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3292" data-mp="2ofvmrnu62ub1">
            Day of month
          </label>
          <div className="relative" data-id="element-3293" data-mp="1wophyu3i48ff">
            <select id={`${idPrefix}-fixed-day`} value={fixedDay} onChange={e => onChange({
          fixedDay: Number(e.target.value)
        })} className={selectClass} data-id="element-3294" data-mp="1wpqznhf0vt64">
              {DAYS.map(day => <option key={day} value={day} data-id="element-3295" data-mp="25rqhgbxv31a4">
                  {formatOrdinal(day)}
                </option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] pointer-events-none" data-id="element-3296" data-mp="3h5c7s0fgj8x8" />
          </div>
          <p className="text-[11px] text-[#94A3B8] mt-1" data-id="element-3297" data-mp="3fp84ks4jm2xc">
            Limited to the 28th so the date exists in every month.
          </p>
        </div>}

      {isAligned && <>
          {isReleaseMode && <div className="mt-5" data-id="element-3298" data-mp="3opwk5r9l4bcc">
              <label htmlFor={`${idPrefix}-cutoff`} className="flex flex-wrap items-center gap-2 text-[13px] text-[#0F172A]" data-id="element-3299" data-mp="3omqvgd4vbbh5">
                <span data-id="element-3300" data-mp="2hn62r54fu1r">A sign-up counts as the next release if it is within</span>
                <input id={`${idPrefix}-cutoff`} type="number" min="0" max="90" value={cutoffDays} onChange={e => onChange({
            cutoffDays: Math.max(0, Number(e.target.value) || 0)
          })} className="w-[72px] h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3301" data-mp="2fahhv4uylfq" />
                <span data-id="element-3302" data-mp="l5s73que90wv">days of it</span>
              </label>
              <p className="text-[11px] text-[#94A3B8] mt-1" data-id="element-3303" data-mp="gacuf284hb21">
                They're charged at sign-up either way. Inside this window, their
                next charge is the release after. Outside it, their next charge
                is the next release.
              </p>
            </div>}

          <div className="mt-4 p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]" aria-live="polite" data-id="element-3304" data-mp="qtdl6i20amll">
            <div className="flex items-center gap-2" data-id="element-3305" data-mp="uufkeyvxaynk">
              <CalendarDays className="w-4 h-4 text-[#0EA5E9]" data-id="element-3306" data-mp="h4lz2qjmmwil" />
              <span className="text-[12px] font-semibold text-[#475569]" data-id="element-3307" data-mp="h3108oqa4rex">
                A member joining today
              </span>
            </div>
            <p className="text-[13px] text-[#0F172A] mt-1 pl-6" data-id="element-3308" data-mp="76xqawp6rohf">
              {describeJoiningToday(billingMode, fixedDay, releaseDates, cutoffDays, frequencies)}
            </p>
          </div>
        </>}

      <p className="text-[11px] text-[#94A3B8] mt-4 pt-3 border-t border-[#F1F5F9]" data-id="element-3309" data-mp="3uam1gxh35ee7">
        Charge time follows your account billing time (Settings &gt; Order
        Processing).
      </p>
    </div>;
}